/**
 * Ρυθμίσεις συσκευής για τον κατάλογο του ταμείου (12/10/2026, αποφάσεις ιδιοκτήτη):
 * - `catalogLayout`: «μία οθόνη» (κατηγορίες επάνω ως φίλτρο, προϊόντα από κάτω) ή «σε βήματα»
 *   (κατηγορίες σε πλήρη οθόνη → πάτημα → προϊόντα, όπως η παλιά διάταξη). Είναι ό,τι έμεινε από
 *   τον παλιό διακόπτη «Κλασική/Νέα»: η εμφάνιση είναι πλέον μία, αλλάζει μόνο ο κατάλογος.
 * - οι κατηγορίες φαίνονται ΟΛΕΣ και, αν είναι πολλές, κυλούν μέσα σε `categoryRows` σειρές·
 * - η κατηγορία «Όλα» είναι προαιρετική·
 * - το χρώμα των κατηγοριών: γεμάτο (προεπιλογή) / απαλό / χωρίς.
 * Μένουν στη συσκευή (localStorage), όπως οι στήλες, και τις διαβάζουν όλοι οι κατάλογοι του
 * ταμείου (take away, τραπέζια, delivery).
 */
import { useSyncExternalStore } from 'react';

export type CategoryColorMode = 'soft' | 'solid' | 'none';
export type CatalogLayout = 'single' | 'steps';

export interface CatalogPrefsV2 {
  catalogLayout: CatalogLayout;
  /** Κουτί «Όλα (N)» δίπλα στις κατηγορίες. */
  showAll: boolean;
  /** Σειρές κατηγοριών πριν αρχίσει η κύλιση. */
  categoryRows: number;
  categoryColor: CategoryColorMode;
}

export const CATALOG_PREFS_KEY = 'xentrix_v2_catalog';
export const CATEGORY_ROW_OPTIONS = [2, 3, 4, 5] as const;
const DEFAULTS: CatalogPrefsV2 = { catalogLayout: 'single', showAll: false, categoryRows: 3, categoryColor: 'solid' };
const EVENT = 'xentrix:v2-catalog-prefs';

let cached: CatalogPrefsV2 | null = null;

const sanitize = (raw: unknown): CatalogPrefsV2 => {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Partial<Record<keyof CatalogPrefsV2, unknown>>;
  const rows = Number(r.categoryRows);
  return {
    catalogLayout: r.catalogLayout === 'steps' ? 'steps' : 'single',
    showAll: r.showAll === true,
    categoryRows: (CATEGORY_ROW_OPTIONS as readonly number[]).includes(rows) ? rows : DEFAULTS.categoryRows,
    categoryColor: r.categoryColor === 'soft' || r.categoryColor === 'none' ? r.categoryColor : 'solid',
  };
};

export function readCatalogPrefsV2(): CatalogPrefsV2 {
  if (cached) return cached;
  try {
    cached = sanitize(JSON.parse(localStorage.getItem(CATALOG_PREFS_KEY) || '{}'));
  } catch {
    cached = { ...DEFAULTS };
  }
  return cached;
}

export function setCatalogPrefsV2(patch: Partial<CatalogPrefsV2>): void {
  cached = sanitize({ ...readCatalogPrefsV2(), ...patch });
  try { localStorage.setItem(CATALOG_PREFS_KEY, JSON.stringify(cached)); } catch { /* μόνο ευκολία */ }
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(EVENT));
}

/** Για tests: ξεχνά την τιμή στη μνήμη (το localStorage το καθαρίζει το ίδιο το test). */
export function resetCatalogPrefsV2Cache(): void {
  cached = null;
}

const subscribe = (onChange: () => void) => {
  window.addEventListener(EVENT, onChange);
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
};

export function useCatalogPrefsV2(): CatalogPrefsV2 {
  return useSyncExternalStore(subscribe, readCatalogPrefsV2, readCatalogPrefsV2);
}

/** Τι «look» παίρνει το ProductCatalog από τη ρύθμιση συσκευής: «σε βήματα» = η παλιά διάταξη. */
export const catalogLookOf = (prefs: Pick<CatalogPrefsV2, 'catalogLayout'>): 'classic' | 'v2' =>
  prefs.catalogLayout === 'steps' ? 'classic' : 'v2';
